import React from 'react';
import {
    FlexDiv,
    Block,
    Heading4,
    TextDefault,
    TextInput,
} from './ui';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    InputLabel,
    TextField,
    Stack,
} from '@mui/material';
import { FlatBlockScoresSection } from './flatBlockScoresSection';
import { ConversationBlockEditor } from './conversationBlockEditor';
import { YesNoListBlockEditor } from './yesNoListBlockEditor';
import { PasswordTesterBlockEditor } from './passwordTesterBlockEditor';
import { DecisionBlockEditor } from './decisionBlockEditor';
import { GroupOfBlocksBlockEditor } from './groupOfBlocksBlockEditor';
import { BlockHiddenBlockEditor } from './blockHiddenBlockEditor';
import { CustomPresentationEditor } from './customPresentationEditor';
import {
    collectPresentationActionIds,
    getArrayActionComponent,
    getArrayActionScore,
    getOptionActionKey,
    getScoringFieldAndList,
    isArrayActionEntry,
    renameTopLevelBlockScoringAction,
    setArrayActionComponent,
    setArrayActionScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';
import {
    questionComponentsSelectValue,
    scoringComponentSelectOptions,
} from './threatAreaScoring';

/**
 * Values for the Threat Area select: company catalog (`threatAreaName`) plus
 * the question's current `area` when it is not in the catalog (legacy / import).
 */
function threatAreaSelectValues(threatAreaOptions, currentArea) {
    const out = [];
    const seen = new Set();
    const add = (v) => {
        if (v == null) return;
        const s = String(v).trim();
        if (!s || seen.has(s)) return;
        seen.add(s);
        out.push(s);
    };
    if (Array.isArray(threatAreaOptions)) {
        for (const o of threatAreaOptions) {
            if (typeof o === 'string') add(o);
            else if (o?.threatAreaName) add(o.threatAreaName);
        }
    }
    add(currentArea);
    if (out.length === 0) {
        add('General');
    }
    return out;
}

/** Blocks listed *before* `currentBlockId` in `ui.blocks` that have an Action ID (valid condition sources). */
function priorBlocksWithActionIds(blocks, currentBlockId) {
    const list = blocks || [];
    const curIdx = list.findIndex((b) => b && b.id === currentBlockId);
    if (curIdx <= 0) return [];
    return list
        .slice(0, curIdx)
        .filter(
            (b) =>
                b &&
                b.actionId != null &&
                String(b.actionId).trim() !== ''
        );
}

function findBlockByActionId(blocks, actionId) {
    const t = String(actionId || '').trim();
    if (!t) return null;
    return (blocks || []).find((b) => String(b.actionId || '') === t) || null;
}

function conditionValueChoices(block) {
    const spec = block ? getScoringFieldAndList(block) : null;
    if (!spec?.list?.length) return null;
    return spec.list.map((opt, i) => ({
        key: getOptionActionKey(opt, i),
        label:
            typeof opt === 'string'
                ? opt
                : String(opt?.text ?? opt?.label ?? opt?.value ?? getOptionActionKey(opt, i)),
    }));
}

function PropertiesPanel({
    variant = 'full',
    selectedBlock,
    selectedPresentation,
    question,
    /** Company threat areas from admin (`catalogStore.availableThreatAreas`). */
    threatAreaOptions,
    /** Full threat area rows with `keyBehaviours` (`catalogStore.threatAreaServerData`). */
    threatAreasCatalog,
    presentationTypes,
    onChangePresentationType,
    onPatchQuestion,
    onUpdateBlock,
    onUpdatePresentation,
    onUpdateQuestion,
}) {
    const showQuestion = variant === 'full' || variant === 'question-only';
    const showSelection =
        variant === 'full' || variant === 'selection-only';

    const scoringComponentOptions = scoringComponentSelectOptions(
        threatAreasCatalog,
        question?.area
    );

    const [draftBlockActionId, setDraftBlockActionId] = React.useState('');
    React.useEffect(() => {
        if (selectedBlock) {
            setDraftBlockActionId(
                selectedBlock.actionId != null
                    ? String(selectedBlock.actionId)
                    : ''
            );
        }
    }, [selectedBlock?.id, selectedBlock?.actionId]);

    const renderBlockProperties = () => {
        if (!selectedBlock) return null;

        return (
            <Block padding={16} gap={16}>
                <Heading4>Block Properties</Heading4>
                
                <FlexDiv
                    column
                    gap={12}
                    $noWrap
                    style={{ minWidth: 0, width: '100%' }}
                >
                    <TextDefault bold>Type: {selectedBlock.type}</TextDefault>

                    <TextField
                        size="small"
                        label="Action ID"
                        fullWidth
                        value={draftBlockActionId}
                        onChange={(e) =>
                            setDraftBlockActionId(e.target.value)
                        }
                        onBlur={() => {
                            if (!selectedBlock) return;
                            const n = draftBlockActionId.trim();
                            const o =
                                selectedBlock.actionId != null
                                    ? String(selectedBlock.actionId).trim()
                                    : '';
                            if (n === o) return;
                            if (!n) {
                                setDraftBlockActionId(o);
                                return;
                            }
                            const taken = question.ui.blocks.some(
                                (b) =>
                                    b.id !== selectedBlock.id &&
                                    String(b.actionId || '').trim() === n
                            );
                            if (taken) {
                                setDraftBlockActionId(o);
                                return;
                            }
                            if (!onPatchQuestion) {
                                onUpdateBlock({ actionId: n });
                                return;
                            }
                            onPatchQuestion((prev) => {
                                const bid = selectedBlock.id;
                                const dup = prev.ui.blocks.some(
                                    (b) =>
                                        b.id !== bid &&
                                        String(b.actionId || '').trim() === n
                                );
                                if (dup) return prev;
                                let actions = { ...(prev.actions || {}) };
                                if (o && actions[o] != null) {
                                    const next = renameTopLevelBlockScoringAction(
                                        actions,
                                        o,
                                        n
                                    );
                                    if (next == null) {
                                        return prev;
                                    }
                                    actions = next;
                                }
                                const blocks = prev.ui.blocks.map((b) =>
                                    b.id === bid ? { ...b, actionId: n } : b
                                );
                                return {
                                    ...prev,
                                    actions,
                                    ui: { ...prev.ui, blocks },
                                };
                            });
                        }}
                        helperText="Unique ID for this block’s scoring. You can rename it if needed."
                        InputLabelProps={{ shrink: true }}
                    />
                    
                    <TextInput
                        label="Title"
                        value={selectedBlock.title || ''}
                        onChange={(e) => onUpdateBlock({ title: e.target.value })}
                        placeholder="Block title"
                    />

                    <TextInput
                        label="Subtitle"
                        value={selectedBlock.pretitle || ''}
                        onChange={(e) =>
                            onUpdateBlock({ pretitle: e.target.value })
                        }
                        placeholder="Optional line above the title"
                    />
                    <TextField
                        size="small"
                        label="Introduction"
                        fullWidth
                        multiline
                        minRows={2}
                        value={selectedBlock.description || ''}
                        onChange={(e) =>
                            onUpdateBlock({ description: e.target.value })
                        }
                        placeholder="Optional supporting text"
                        InputLabelProps={{ shrink: true }}
                    />

                    {(() => {
                        const priorBlocks = priorBlocksWithActionIds(
                            question.ui.blocks,
                            selectedBlock.id
                        );
                        const cond = selectedBlock.condition;
                        const hasCondition = !!(
                            cond && String(cond.actionId || '').trim()
                        );
                        const refBlock = findBlockByActionId(
                            question.ui.blocks,
                            cond?.actionId
                        );
                        const valueChoices = refBlock
                            ? conditionValueChoices(refBlock)
                            : null;
                        const refInPrior =
                            hasCondition &&
                            priorBlocks.some(
                                (b) =>
                                    String(b.actionId || '') ===
                                    String(cond?.actionId || '')
                            );
                        return (
                            <Stack spacing={1.5} sx={{ minWidth: 0, width: '100%' }}>
                                <TextDefault bold>When to show this block</TextDefault>
                                <TextDefault lighter>
                                    Optional: show only after the learner picks a
                                    specific answer on a question that appears
                                    earlier in this scenario (same as in the live
                                    assessment).
                                </TextDefault>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={hasCondition}
                                            disabled={!priorBlocks.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    const ref = priorBlocks[0];
                                                    const aid = String(
                                                        ref?.actionId || ''
                                                    );
                                                    const choices =
                                                        conditionValueChoices(
                                                            ref
                                                        );
                                                    const val =
                                                        choices &&
                                                        choices.length
                                                            ? choices[0].key
                                                            : '';
                                                    onUpdateBlock({
                                                        condition: {
                                                            actionId: aid,
                                                            value: val,
                                                        },
                                                    });
                                                } else {
                                                    onUpdateBlock({
                                                        condition: null,
                                                    });
                                                }
                                            }}
                                        />
                                    }
                                    label="Only show when an earlier question is answered a certain way"
                                />
                                {!priorBlocks.length ? (
                                    <TextDefault lighter>
                                        Place a block with an Action ID above this
                                        one in the scenario, or use Move left in
                                        the block header to reorder.
                                    </TextDefault>
                                ) : null}
                                {hasCondition && !refInPrior ? (
                                    <TextDefault lighter>
                                        This condition refers to a block that is
                                        not before this one. Pick an earlier block
                                        below, or use Move left / Move right on the
                                        canvas to reorder blocks.
                                    </TextDefault>
                                ) : null}
                                {hasCondition ? (
                                    <>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                        >
                                            <InputLabel id="cond-ref-action">
                                                Earlier in scenario (Action ID)
                                            </InputLabel>
                                            <Select
                                                labelId="cond-ref-action"
                                                label="Earlier in scenario (Action ID)"
                                                value={
                                                    refInPrior
                                                        ? String(
                                                              cond?.actionId ||
                                                                  ''
                                                          )
                                                        : ''
                                                }
                                                onChange={(e) => {
                                                    const nextId =
                                                        e.target.value;
                                                    const nb =
                                                        findBlockByActionId(
                                                            question.ui.blocks,
                                                            nextId
                                                        );
                                                    const choices = nb
                                                        ? conditionValueChoices(
                                                              nb
                                                          )
                                                        : null;
                                                    const nextVal =
                                                        choices &&
                                                        choices.length &&
                                                        choices.some(
                                                            (c) =>
                                                                c.key ===
                                                                cond?.value
                                                        )
                                                            ? cond.value
                                                            : '';
                                                    onUpdateBlock({
                                                        condition: {
                                                            actionId: nextId,
                                                            value: nextVal,
                                                        },
                                                    });
                                                }}
                                            >
                                                {priorBlocks.map((b) => (
                                                    <MenuItem
                                                        key={b.id}
                                                        value={String(
                                                            b.actionId
                                                        )}
                                                    >
                                                        {b.type} —{' '}
                                                        {b.actionId}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {valueChoices &&
                                        valueChoices.length ? (
                                            <FormControl
                                                fullWidth
                                                size="small"
                                            >
                                                <InputLabel id="cond-val">
                                                    Answer value
                                                </InputLabel>
                                                <Select
                                                    labelId="cond-val"
                                                    label="Answer value"
                                                    value={(() => {
                                                        const v = String(
                                                            cond?.value ?? ''
                                                        );
                                                        return valueChoices.some(
                                                            (c) => c.key === v
                                                        )
                                                            ? v
                                                            : '';
                                                    })()}
                                                    onChange={(e) =>
                                                        onUpdateBlock({
                                                            condition: {
                                                                actionId:
                                                                    String(
                                                                        cond?.actionId ||
                                                                            ''
                                                                    ),
                                                                value: e.target
                                                                    .value,
                                                            },
                                                        })
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        <em>Choose…</em>
                                                    </MenuItem>
                                                    {valueChoices.map((c) => (
                                                        <MenuItem
                                                            key={c.key}
                                                            value={c.key}
                                                        >
                                                            {c.label} ({c.key})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Answer value"
                                                value={String(
                                                    cond?.value ?? ''
                                                )}
                                                onChange={(e) =>
                                                    onUpdateBlock({
                                                        condition: {
                                                            actionId: String(
                                                                cond?.actionId ||
                                                                    ''
                                                            ),
                                                            value: e.target
                                                                .value,
                                                        },
                                                    })
                                                }
                                                helperText="Use the same value as the option on the earlier question (e.g. wifi)."
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                    </>
                                ) : null}
                            </Stack>
                        );
                    })()}

                    <ConversationBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <FlatBlockScoresSection
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <PasswordTesterBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <DecisionBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <GroupOfBlocksBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <BlockHiddenBlockEditor
                        selectedBlock={selectedBlock}
                        onUpdateBlock={onUpdateBlock}
                    />

                    {selectedBlock.type === 'numericalscale' ? (
                        <FlexDiv column gap={8}>
                            <FlexDiv alignCenter gap={12}>
                                <TextInput
                                    label="Min Value"
                                    type="number"
                                    value={selectedBlock.min || 1}
                                    onChange={(e) => onUpdateBlock({ min: parseInt(e.target.value) })}
                                />
                                <TextInput
                                    label="Max Value"
                                    type="number"
                                    value={selectedBlock.max || 10}
                                    onChange={(e) => onUpdateBlock({ max: parseInt(e.target.value) })}
                                />
                            </FlexDiv>
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'yes-no-list' ? (
                        <YesNoListBlockEditor
                            selectedBlock={selectedBlock}
                            question={question}
                            scoringComponentOptions={scoringComponentOptions}
                            onPatchQuestion={onPatchQuestion}
                            onUpdateBlock={onUpdateBlock}
                        />
                    ) : null}

                    {selectedBlock.type === 'questionwithimage' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Image URL"
                                value={selectedBlock.imageUrl || ''}
                                onChange={(e) => onUpdateBlock({ imageUrl: e.target.value })}
                                placeholder="https://example.com/image.jpg"
                            />
                        </FlexDiv>
                    ) : null}

                </FlexDiv>
            </Block>
        );
    };

    const renderPresentationProperties = () => {
        if (!selectedPresentation) return null;

        return (
            <Block padding={16} gap={16}>
                <Heading4>Presentation Properties</Heading4>

                <FlexDiv
                    column
                    gap={12}
                    $noWrap
                    style={{ minWidth: 0, width: '100%' }}
                >
                    {presentationTypes && onChangePresentationType ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="presentation-type-label">
                                Presentation type
                            </InputLabel>
                            <Select
                                labelId="presentation-type-label"
                                label="Presentation type"
                                value={selectedPresentation.type || ''}
                                onChange={(e) =>
                                    onChangePresentationType(e.target.value)
                                }
                            >
                                {Object.keys(presentationTypes).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {presentationTypes[key]?.name || key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextDefault bold>
                            Type: {selectedPresentation.type}
                        </TextDefault>
                    )}

                    <TextInput
                        label="Title"
                        value={selectedPresentation.title || ''}
                        onChange={(e) => onUpdatePresentation({ title: e.target.value })}
                        placeholder="Presentation title"
                    />

                    <TextInput
                        label="Subtitle"
                        value={selectedPresentation.pretitle || ''}
                        onChange={(e) =>
                            onUpdatePresentation({ pretitle: e.target.value })
                        }
                        placeholder="Optional line above the title"
                    />

                    {selectedPresentation.type !== 'email' &&
                    selectedPresentation.type !== 'custom' ? (
                        <TextField
                            size="small"
                            label="Introduction"
                            fullWidth
                            multiline
                            minRows={2}
                            value={selectedPresentation.description || ''}
                            onChange={(e) =>
                                onUpdatePresentation({
                                    description: e.target.value,
                                })
                            }
                            placeholder="Optional text below the title"
                            InputLabelProps={{ shrink: true }}
                        />
                    ) : null}

                    {selectedPresentation.type === 'email' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Subject"
                                value={selectedPresentation.subject || ''}
                                onChange={(e) => onUpdatePresentation({ subject: e.target.value })}
                                placeholder="Email subject"
                            />
                            <TextInput
                                label="Sender"
                                value={selectedPresentation.sender || ''}
                                onChange={(e) => onUpdatePresentation({ sender: e.target.value })}
                                placeholder="sender@example.com"
                            />
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                label="Content"
                                value={selectedPresentation.content || ''}
                                onChange={(e) => onUpdatePresentation({ content: e.target.value })}
                                placeholder="Email content"
                            />
                        </FlexDiv>
                    ) : null}

                    {selectedPresentation.type === 'message' || selectedPresentation.type === 'password' || selectedPresentation.type === 'question' ? (
                        <TextField
                            multiline
                            rows={4}
                            fullWidth
                            label="Content"
                            value={selectedPresentation.content || ''}
                            onChange={(e) => onUpdatePresentation({ content: e.target.value })}
                            placeholder="Content text"
                        />
                    ) : null}

                    {selectedPresentation.type === 'illustration' || selectedPresentation.type === 'interactive-img' ? (
                        <TextInput
                            label="Image URL"
                            value={selectedPresentation.imageUrl || ''}
                            onChange={(e) => onUpdatePresentation({ imageUrl: e.target.value })}
                            placeholder="https://example.com/image.jpg"
                        />
                    ) : null}

                    {selectedPresentation.type === 'video' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Video URL"
                                value={selectedPresentation.videoUrl || ''}
                                onChange={(e) => onUpdatePresentation({ videoUrl: e.target.value })}
                                placeholder="https://example.com/video.mp4"
                            />
                            <FlexDiv alignCenter gap={8}>
                                <Checkbox
                                    checked={selectedPresentation.autoplay || false}
                                    onChange={(e) => onUpdatePresentation({ autoplay: e.target.checked })}
                                />
                                <TextDefault>Autoplay</TextDefault>
                            </FlexDiv>
                        </FlexDiv>
                    ) : null}

                    <CustomPresentationEditor
                        presentation={selectedPresentation}
                        onUpdatePresentation={onUpdatePresentation}
                    />

                    {(() => {
                        const ids = collectPresentationActionIds(
                            selectedPresentation
                        ).filter((id) =>
                            isArrayActionEntry(question.actions, id)
                        );
                        if (!ids.length) return null;
                        return (
                            <FlexDiv
                                column
                                gap={10}
                                $noWrap
                                style={{ minWidth: 0, width: '100%' }}
                            >
                                <TextDefault bold>
                                    Presentation interaction scores
                                </TextDefault>
                                <TextDefault lighter>
                                    Optional scores for interactive steps (for
                                    example hover or click in a simulation).
                                </TextDefault>
                                {ids.map((id) => (
                                    <Stack
                                        key={id}
                                        direction="column"
                                        spacing={2}
                                        alignItems="stretch"
                                        sx={{ width: '100%', minWidth: 0 }}
                                    >
                                        <TextField
                                            size="small"
                                            sx={{ width: '100%', maxWidth: 280 }}
                                            label="Score"
                                            helperText={`Step: ${id}`}
                                            type="number"
                                            inputProps={{ step: 'any' }}
                                            value={getArrayActionScore(
                                                question.actions,
                                                id
                                            )}
                                            onChange={(e) => {
                                                if (!onUpdateQuestion) return;
                                                onUpdateQuestion({
                                                    actions: setArrayActionScore(
                                                        question.actions,
                                                        id,
                                                        e.target.value,
                                                        question
                                                    ),
                                                });
                                            }}
                                        />
                                        <Stack
                                            sx={{
                                                width: '100%',
                                                minWidth: 0,
                                            }}
                                        >
                                            <ScoringComponentSelect
                                                label="Learning goal"
                                                componentOptions={
                                                    scoringComponentOptions
                                                }
                                                value={getArrayActionComponent(
                                                    question.actions,
                                                    id,
                                                    question
                                                )}
                                                onChange={(comp) => {
                                                    if (!onUpdateQuestion) return;
                                                    onUpdateQuestion({
                                                        actions:
                                                            setArrayActionComponent(
                                                                question.actions,
                                                                id,
                                                                comp,
                                                                question
                                                            ),
                                                    });
                                                }}
                                            />
                                            <TextDefault lighter>
                                                Step: {id}
                                            </TextDefault>
                                        </Stack>
                                    </Stack>
                                ))}
                            </FlexDiv>
                        );
                    })()}
                </FlexDiv>
            </Block>
        );
    };

    const renderQuestionProperties = () => {
        const currentArea =
            question.area != null && String(question.area).trim() !== ''
                ? String(question.area).trim()
                : 'General';
        const areaChoices = threatAreaSelectValues(
            threatAreaOptions,
            currentArea
        );
        const selectAreaValue = areaChoices.includes(currentArea)
            ? currentArea
            : areaChoices[0];

        return (
            <Block padding={16} gap={16}>
                <Heading4>Question Properties</Heading4>
                
                <FlexDiv
                    column
                    gap={12}
                    $noWrap
                    style={{ minWidth: 0, width: '100%' }}
                >
                    <TextInput
                        label="Question Name"
                        value={question.name || ''}
                        onChange={(e) => onUpdateQuestion({ name: e.target.value })}
                        placeholder="Question name"
                    />

                    {question.owner_unique_id ? (
                        <TextField
                            fullWidth
                            label="Owner (company or partner GUID)"
                            value={question.owner_unique_id}
                            InputProps={{ readOnly: true }}
                            helperText="Set by the server when you create the question or save a copy."
                        />
                    ) : null}
                    
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        label="Description"
                        value={question.description || ''}
                        onChange={(e) => onUpdateQuestion({ description: e.target.value })}
                        placeholder="Question description"
                    />
                    
                    <FormControl fullWidth>
                        <InputLabel>Threat Area</InputLabel>
                        <Select
                            label="Threat Area"
                            value={selectAreaValue}
                            onChange={(e) => onUpdateQuestion({ area: e.target.value })}
                        >
                            {areaChoices.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl fullWidth>
                        <InputLabel>Difficulty</InputLabel>
                        <Select
                            value={question.difficulty || 'basic'}
                            onChange={(e) => onUpdateQuestion({ difficulty: e.target.value })}
                        >
                            <MenuItem value="basic">Basic</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="advanced">Advanced</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Scoring component</InputLabel>
                        <Select
                            label="Scoring component"
                            value={questionComponentsSelectValue(
                                question.components,
                                scoringComponentOptions
                            )}
                            onChange={(e) =>
                                onUpdateQuestion({ components: e.target.value })
                            }
                        >
                            {scoringComponentOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextDefault lighter>
                        Key behaviours for the selected threat area (admin →
                        Threat areas). Used as the default{' '}
                        <code>component</code> on new score rows in{' '}
                        <code>actions</code>.
                    </TextDefault>
                </FlexDiv>
            </Block>
        );
    };

    return (
        <div className="properties-panel">
            <FlexDiv
                column
                gap={16}
                $noWrap
                style={{ minWidth: 0, width: '100%' }}
            >
                {showQuestion ? renderQuestionProperties() : null}
                {showSelection ? renderBlockProperties() : null}
                {showSelection ? renderPresentationProperties() : null}

                {showSelection &&
                !selectedBlock &&
                !selectedPresentation ? (
                    <Block padding={16}>
                        <TextDefault lighter>
                            Select a block or presentation on the canvas to edit
                            its properties.
                        </TextDefault>
                    </Block>
                ) : null}
            </FlexDiv>
        </div>
    );
}

export default PropertiesPanel;
